import { Link } from "@tanstack/react-router";
import { ChevronRight, Loader2, Plus, SearchX, UserPlus } from "lucide-react";
import { Button } from "@/frontend/components/ui/button";
import { VirtualList } from "@/frontend/components/ui/virtual-list";
import { getInitials, formatDateTime } from "@/frontend/utils/shared";
import { cn } from "@/frontend/lib/utils";
import { lastSyncLabel } from "@/frontend/utils/dashboard/patient-status";
import { PatientStatusBadge } from "./PatientStatusBadge";
import { SourceLogoStack } from "./SourceLogoStack";
import { PatientTableHeader } from "./PatientTableHeader";
import { PATIENT_TABLE_GRID, ROW_HEIGHT } from "@/frontend/types/dashboard/patients";
import { avatarTint } from "@/frontend/utils/dashboard/patient-table";
import type { PatientTableHeaderProps } from "@/frontend/types/dashboard/patients";
import type { PatientListRow } from "@/server/types/patient-status";

/**
 * One patient row, a single Link spanning every cell so the whole row navigates
 * into the patient's Summary and counts as one keyboard tab stop.
 */
function PatientRow({ row }: { row: PatientListRow }) {
  return (
    <Link
      to="/dashboard/patients/$patientId/summary"
      params={{ patientId: row.id }}
      className={cn(
        PATIENT_TABLE_GRID,
        "group h-14 border-b px-6 transition-colors hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none",
      )}
    >
      <span className="flex min-w-0 items-center gap-3 pr-4">
        <span
          className={cn(
            "flex size-8 shrink-0 items-center justify-center rounded-full text-caption font-medium",
            avatarTint(row.id),
          )}
        >
          <span data-patient>{getInitials(row.name, row.communicationEmail)}</span>
        </span>
        <span className="min-w-0">
          <span className="block truncate text-caption font-medium" data-patient>
            {row.name}
          </span>
          <span className="block truncate text-caption text-muted-foreground" data-patient>
            {row.communicationEmail ?? "-"}
          </span>
        </span>
      </span>
      <span className="pr-4">
        <SourceLogoStack healthSystems={row.healthSystems} totalCount={row.healthSystemCount} />
      </span>
      <span className="pr-4 text-caption font-medium tabular-nums">
        {row.recordCount > 0 ? row.recordCount.toLocaleString() : "—"}
      </span>
      <span className="pr-4">
        <PatientStatusBadge status={row.status} needsAttention={row.needsAttention} />
      </span>
      <span className="truncate whitespace-nowrap pr-4 text-caption text-muted-foreground">
        {lastSyncLabel(row.status, row.lastActivityAt, formatDateTime(row.lastActivityAt))}
      </span>
      <span className="flex items-center justify-center text-muted-foreground transition-colors group-hover:text-foreground">
        <ChevronRight className="size-4" />
      </span>
    </Link>
  );
}

/**
 * The workspace patient table, a fixed header row with sort cycling over a
 * DOM-windowed, infinitely loading body. Only fetched chunks are ever in memory,
 * and `onLoadMore` pulls the next one as the scroll nears the loaded end.
 */
export function PatientTable({
  rows,
  listKey,
  isLoading,
  hasFilters,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  isLoadMoreError,
  onRetryLoadMore,
  header,
  onAddPatient,
  onClearFilters,
}: {
  rows: PatientListRow[];
  /** Identity of the current filter/sort set. Changing it remounts the list, resetting scroll and the load-more arm. */
  listKey: string;
  isLoading: boolean;
  hasFilters: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  /** True when the last chunk fetch failed; the list shows a retry row instead of silently stopping. */
  isLoadMoreError?: boolean;
  onRetryLoadMore?: () => void;
  header: PatientTableHeaderProps;
  onAddPatient: () => void;
  onClearFilters?: () => void;
}) {
  let body: React.ReactNode;
  if (isLoading && rows.length === 0) {
    body = (
      <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto py-16">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  } else if (rows.length === 0 && hasFilters) {
    // Two distinct empty states: a filtered-out result set offers a way back to
    // everyone, while a genuinely empty workspace offers the primary action.
    body = (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center overflow-auto py-16 text-center">
        <SearchX className="mb-3 size-6 text-muted-foreground opacity-60" />
        <p className="text-caption font-medium">No patients match these filters</p>
        <p className="mt-1 max-w-sm text-caption text-muted-foreground">
          Try a different search, or clear the filters to see everyone.
        </p>
        {onClearFilters && (
          <Button variant="outline" className="mt-4 h-8 gap-1.5" onClick={onClearFilters}>
            Clear filters
          </Button>
        )}
      </div>
    );
  } else if (rows.length === 0) {
    body = (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center overflow-auto py-16 text-center">
        <UserPlus className="mb-3 size-6 text-muted-foreground opacity-60" />
        <p className="text-caption font-medium">No patients yet</p>
        <p className="mt-1 max-w-sm text-caption text-muted-foreground">
          Add the first person whose records you want to bring together.
        </p>
        <Button className="mt-4 h-8 gap-1.5" onClick={onAddPatient}>
          <Plus className="size-4" />
          Add patient
        </Button>
      </div>
    );
  } else {
    body = (
      <VirtualList
        key={listKey}
        rows={rows}
        renderRow={(row) => <PatientRow row={row} />}
        estimateSize={ROW_HEIGHT}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        onLoadMore={onLoadMore}
        isError={isLoadMoreError}
        onRetry={onRetryLoadMore}
        className="min-h-0 flex-1"
      />
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden border-b">
      <PatientTableHeader {...header} />
      {body}
    </div>
  );
}
